/**
 * Tiny inline "Follow venue" button. Self-contained: handles its own follow
 * state and click handling. Used on event cards alongside the artist-follow
 * affordance, and on the Venue page header.
 *
 * Props:
 *   venueId        — required, the canonical venue_id (e.g. "tm_KovZ9177BiV")
 *   initialIsFollowing — optional bool, hydrate from server if known
 *   size           — 'sm' (default, for cards) | 'md' (for venue page header)
 *   onToggle       — optional callback(isFollowing) for parents that care
 */

import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import VenuesService from '../../services/venuesService';
import AuthService from '../../services/authService';
import Icon from './Icon';

const SIZES = {
  sm: 'px-1.5 py-0.5 text-xs gap-0.5',
  md: 'px-3 py-1.5 text-sm gap-1.5'
};

const ICON_SIZES = {
  sm: 'w-3 h-3',
  md: 'w-4 h-4'
};

const VenueFollowButton = ({ venueId, initialIsFollowing = false, size = 'sm', onToggle }) => {
  const { t } = useTranslation();
  const [isFollowing, setIsFollowing] = useState(initialIsFollowing);
  const [busy, setBusy] = useState(false);

  const handleClick = useCallback(async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (busy || !venueId) return;
    if (!AuthService.isAuthenticated()) {
      // No auth - bounce to login; the Venue page works for anonymous viewers
      // but the follow action does not.
      window.location.href = '/login';
      return;
    }
    setBusy(true);
    const action = isFollowing ? VenuesService.unfollow : VenuesService.follow;
    const result = await action(venueId);
    setBusy(false);
    if (result?.success) {
      const next = !isFollowing;
      setIsFollowing(next);
      if (onToggle) onToggle(next);
    }
  }, [venueId, isFollowing, busy, onToggle]);

  if (!venueId) return null;

  const cls = SIZES[size] || SIZES.sm;
  const iconCls = ICON_SIZES[size] || ICON_SIZES.sm;

  return (
    <button
      onClick={handleClick}
      disabled={busy}
      className={
        `flex-shrink-0 flex items-center rounded-full font-medium transition-colors ${cls} ` +
        (isFollowing
          ? 'bg-success-light text-success-dark hover:bg-success hover:text-content-inverse'
          : 'bg-primary-lighter text-primary hover:bg-primary hover:text-content-inverse')
      }
      title={isFollowing ? t('venueFollowButton.unfollowTitle') : t('venueFollowButton.followTitle')}
      aria-label={isFollowing ? t('venueFollowButton.unfollowAriaLabel') : t('venueFollowButton.followAriaLabel')}
    >
      <Icon name={isFollowing ? 'check' : 'plus'} className={iconCls} />
      <span className={size === 'sm' ? 'hidden sm:inline' : ''}>
        {isFollowing ? t('venueFollowButton.followingLabel') : t('venueFollowButton.followLabel')}
      </span>
    </button>
  );
};

export default React.memo(VenueFollowButton);
